import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import * as SharedStyled from '@common/styles';
import { StyledSVG } from '@common/styles';
import SubmissionFlowFooter from '@common/components/SubmissionFlowFooter/SubmissionFlowFooter';
import DatapharmTable from '@common/components/DatapharmTable/DatapharmTable';
import { NotifyModal } from '@common/components/NotifyModal';
import { ModalTypes } from '@common/components/NotifyModal/NotifyModal';
import { paths, routes } from '../../../routing/routes';
import { globalRoutes } from '@routing/routes';
import * as Styled from './styles';
import { IBroadcastRecipient, OptedInStatus } from '../types';
import { addBroadcastRecipient, removeBroadcastRecipient, sendOptInRequest } from '../../../services/broadcastService';
import { prepareBroadcastRecipientsColumns } from './helpers';
import { useFetchBroadcastRecipients, broadcastRecipientsQueryKey } from './hooks/useFetchBroadcastRecipients';
import { useUploadBroadcastRecipients } from './hooks/useUploadBroadcastRecipients';
import { useFetchSubmissionTaskData } from '@common/hooks/useFetchSubmissionTaskData';
import { DocumentType } from '@common/types';
import { PleaseWaitOverlay } from '@common/components/PleaseWaitOverlay';
import { SubmissionTaskStatuses } from '@common/services/types';
import { AddEmailAddressForm } from './AddEmailAddressForm';
import { UploadContactListModal } from './UploadContactListModal';

const BroadcastAddressBook = () => {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [selectedRecipientIds, setSelectedRecipientIds] = useState<string[]>([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [recipientToDelete, setRecipientToDelete] = useState<{ id: string; email: string } | null>(null);
  const [showConsentWarningModal, setShowConsentWarningModal] = useState(false);
  const [recipientToOptIn, setRecipientToOptIn] = useState<{ id: string; email: string } | null>(null);
  const [uploadResultModal, setUploadResultModal] = useState<{ isOpen: boolean; isSuccess: boolean; message: string }>({ 
    isOpen: false, 
    isSuccess: false, 
    message: '' 
  });
  const [uploadModalOpen, setUploadModalOpen] = useState(false);

  // Fetch and validate task data
  const { submissionTask, isFetchingSubmissionTaskData } = useFetchSubmissionTaskData(taskId || '');

  useEffect(() => {
    if (isFetchingSubmissionTaskData) return;

    const isPublishedSmpcTask =
      submissionTask?.documentType === DocumentType.Smpc &&
      submissionTask?.status === SubmissionTaskStatuses.Published;

    const shouldRedirect = (submissionTask && !isPublishedSmpcTask) || (!submissionTask && taskId);

    if (shouldRedirect) {
      navigate(globalRoutes.dashboard);
    }
  }, [submissionTask, isFetchingSubmissionTaskData, navigate, taskId]);

  const { items: recipients, isLoading: isLoadingRecipients } = useFetchBroadcastRecipients();
  const { uploadRecipients, isUploading } = useUploadBroadcastRecipients();

  useEffect(() => {
    const state = location.state as { selectedRecipientIds?: string[] } | undefined;
    if (state?.selectedRecipientIds) {
      setSelectedRecipientIds(state.selectedRecipientIds);
    }
  }, [location.state]);

  const handleAddEmailAddress = async (data: { emailAddress: string }) => {
    const emailInput = data.emailAddress.trim();

    const emails = emailInput
      .split(';')
      .map((email) => email.trim())
      .filter((email) => email.length > 0);

    await addBroadcastRecipient(emails);
    await queryClient.invalidateQueries({ queryKey: [broadcastRecipientsQueryKey] });
  };

  const handleUploadClick = () => {
    setUploadModalOpen(true);
  };

  const handleUploadModalConfirm = async (file: File | null) => {
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      setUploadModalOpen(false);
      setUploadResultModal({ isOpen: true, isSuccess: false, message: 'Please upload a CSV file.' });
      return;
    }

    setUploadModalOpen(false);

    const result = await uploadRecipients(file);
    setUploadResultModal({ isOpen: true, isSuccess: result.isSuccess, message: result.message });
  };

  const handleToggleSelection = (recipientId: string, selected: boolean) => {
    setSelectedRecipientIds((prev) =>
      selected ? [...prev, recipientId] : prev.filter((id) => id !== recipientId),
    );
  };

  const handleOptIn = (recipientId: string, email: string) => {
    setRecipientToOptIn({ id: recipientId, email });
  };

  const handleConfirmOptIn = async () => {
    if (!recipientToOptIn) return;

    await sendOptInRequest(recipientToOptIn.id);
    await queryClient.invalidateQueries({ queryKey: [broadcastRecipientsQueryKey] });
    setRecipientToOptIn(null);
  };

  const handleCancelOptIn = () => {
    setRecipientToOptIn(null);
  };

  const handleDeleteClick = (recipientId: string, email: string) => {
    setRecipientToDelete({ id: recipientId, email });
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!recipientToDelete) return;

    await removeBroadcastRecipient(recipientToDelete.id);
    await queryClient.invalidateQueries({ queryKey: [broadcastRecipientsQueryKey] });
    setDeleteModalOpen(false);
    setRecipientToDelete(null);
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setRecipientToDelete(null);
  };

  const getSelectedRecipients = () => {
    return recipients.filter((r: IBroadcastRecipient) => selectedRecipientIds.includes(r.id));
  };

  const navigateToSend = () => {
    const selectedRecipients = getSelectedRecipients();
    navigate(`${routes.changeBroadcast}/${taskId}/${paths.changeBroadcastSend}`, {
      state: { recipients: selectedRecipients, selectedRecipientIds },
    });
  };

  const handleConfirmRecipientsSelection = () => {
    const selectedRecipients = getSelectedRecipients();

    // Check if any selected recipients have not given consent
    const hasNoConsentRecipients = selectedRecipients.some(
      (r) => r.status === OptedInStatus.Pending || r.status === OptedInStatus.Expired || r.status === OptedInStatus.NotSent,
    );

    if (hasNoConsentRecipients) {
      setShowConsentWarningModal(true);
    } else {
      navigateToSend();
    }
  };

  const handleContinueWithWarning = () => {
    setShowConsentWarningModal(false);
    navigateToSend();
  };

  const handleCancelWarning = () => {
    setShowConsentWarningModal(false);
  };

  const hasNoSelection = selectedRecipientIds.length === 0;

  const columns = prepareBroadcastRecipientsColumns({
    selectedRecipientIds,
    onToggleSelection: handleToggleSelection,
    onOptIn: handleOptIn,
    onDelete: handleDeleteClick,
  });

  return isFetchingSubmissionTaskData ? (
    <PleaseWaitOverlay />
  ) : (
    <>
      <SharedStyled.SubmissionFlowWrap>
        <SharedStyled.SubmissionFlowInner>
          <SharedStyled.PageTitle>Send broadcast</SharedStyled.PageTitle>
          <Styled.SectionsWrap>
            <Styled.SectionWithAction>
              <Styled.SectionHeaderRow>
                <Styled.SectionHeadingText>Add email address</Styled.SectionHeadingText>
                <a href={process.env.REACT_APP_HELP_CHANGE_BROADCAST_URL} target="_blank" rel="noreferrer">
                  <StyledSVG src="/icons/info_circle.svg" />
                </a>
              </Styled.SectionHeaderRow>
              <Styled.SectionHint>This address book is shared with all Publisher users from your company. Changes will impact the shared address book.</Styled.SectionHint>
              <Styled.SectionHint>To add multiple email addresses, separate each with a semi-colon. By adding these addresses, you confirm that they have consented to receive broadcast messages.</Styled.SectionHint>
              <AddEmailAddressForm onSubmit={handleAddEmailAddress} isDisabled={isLoadingRecipients} />
            </Styled.SectionWithAction>
            <Styled.SectionWithAction>
              <Styled.SectionHeaderRow>
                <Styled.SectionHeadingText>Address book</Styled.SectionHeadingText>
                <Styled.UploadButton onClick={handleUploadClick} disabled={isLoadingRecipients}>
                  Upload contact list
                </Styled.UploadButton>
              </Styled.SectionHeaderRow>
              <Styled.AddressContent>
                {recipients.length === 0 ? (
                  <>
                    <Styled.EmptyState>No contacts added.</Styled.EmptyState>
                    <Styled.SectionHint>
                      Enter email addresses above to add them as recipients of this broadcast.
                    </Styled.SectionHint>
                  </>
                ) : (
                  <Styled.TableWrapper>
                    <DatapharmTable columns={columns} documents={recipients} showLoader={isLoadingRecipients} />
                  </Styled.TableWrapper>
                )}
              </Styled.AddressContent>
            </Styled.SectionWithAction>
          </Styled.SectionsWrap>
        </SharedStyled.SubmissionFlowInner>
      </SharedStyled.SubmissionFlowWrap>
      <SubmissionFlowFooter
        continueText="Confirm recipients"
        onContinue={handleConfirmRecipientsSelection}
        continueDisabled={hasNoSelection}
        errorText={hasNoSelection ? 'Select at least one email address to continue' : undefined}
      />
      <NotifyModal
        title="Are you sure you want to delete this recipient?"
        isOpen={deleteModalOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        confirmText="Delete recipient"
      >
        <p>Deleting this recipient will delete it from the address book for your whole organisation and for future broadcasts.</p>
        <p>
          You can choose not to send this broadcast to this recipient by unticking the check box in the "Send this broadcast"
          column.
        </p>
      </NotifyModal>

      <NotifyModal
        title="Some recipients have not given consent to receive broadcasts"
        isOpen={showConsentWarningModal}
        onClose={handleCancelWarning}
        onConfirm={handleContinueWithWarning}
        confirmText="Continue"
        cancelText="Close"
      >
        <p>These recipients will be sent an email to ask for their consent before the broadcast email is sent to them.</p>
        <p>
          The broadcast email will not be sent to those recipients until they have given their consent. Other recipients will
          receive the broadcast as expected.
        </p>
      </NotifyModal>

      <NotifyModal
        title="Send opt-in request?"
        isOpen={!!recipientToOptIn}
        onClose={handleCancelOptIn}
        onConfirm={handleConfirmOptIn}
        confirmText="Send request"
        cancelText="Cancel"
      >
        <p>
          The user <strong>{recipientToOptIn?.email}</strong> has previously unsubscribed. If you try to send them a broadcast, they will be
          sent an opt-in message.
        </p>
        <p>If you don't wish to continue, you can cancel and remove this user from your address book.</p>
      </NotifyModal>

      <NotifyModal
        title={uploadResultModal.isSuccess ? 'Upload Successful' : 'Upload Error'}
        isOpen={uploadResultModal.isOpen}
        onClose={() => setUploadResultModal({ isOpen: false, isSuccess: false, message: '' })}
        cancelText="Close"
        modalType={uploadResultModal.isSuccess ? ModalTypes.success : ModalTypes.error}
      >
        <p>{uploadResultModal.message}</p>
      </NotifyModal>

      <UploadContactListModal
        isOpen={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onConfirm={handleUploadModalConfirm}
        isUploading={isUploading}
      />
    </>
  );
};

export default BroadcastAddressBook;
